import moment from 'moment'
import { BuilderCampaignType, CampaignCreateWorkflow } from 'workflows/CampaignCreateWorkflow'

import { AudienceFootprint, Campaign, CampaignApprovalResult, CustomerOptimizationStrategy, DayPartitioning, DeviceSettings, FrequencyUnit, IGeoTargeting, InventorySettings, SegmentTarget, ThirdPartyAudience, UpdateAdvancedTargeting, UpdateCampaignNameAndBudgetResponse, UpdateTvTargeting } from 'api/interfaces'
import { IntervalUnit } from 'api/interfaces/Bundle'
import { PaymentInfo } from 'api/interfaces/PaymentInfo'
import { CampaignUpdateRequest } from 'api/interfaces/requests/CampaignUpdateRequest'
import { CampaignUpdateSavedForLaterRequest } from 'api/interfaces/requests/CampaignUpdateSavedForLaterRequest'
import { masterCampaignsApi } from 'api/rtkQueryApi/platform/masterCampaignsApi'
import { mobileDashboardApi } from 'api/rtkQueryApi/platform/mobileDashboardApi'
import { defaultTransformFn, platformApi } from 'api/rtkQueryApi/platform/platformApi'
import { LocationType } from 'components/targeters/GeoSelectionTargeter'
import { AdPaymentType } from 'features/builders/internet/retail/builderInternetRetailSlice'

import { CampaignDelivery } from '../../interfaces/CampaignDelivery'
import { ReserveSettings } from '../../interfaces/ReserveSettings'

export interface IGetCampaignsQuery {
  accountId: string
  isAgency?: boolean
}

export interface IGetCampaignQuery {
  accountId: string
  campaignId?: string
  isAgency?: boolean
}

export interface IAudienceTargeting {
  ageRanges: string[]
  genders: string[]
  householdIncomes: string[]
  thirdPartyAudiences: ThirdPartyAudience[]
  segments: SegmentTarget[]
}

export interface AdvancedTargetingOptions {
  dayParts: DayPartitioning
  inventorySettings: InventorySettings
  deviceSettings: DeviceSettings
  frequency: number
  frequencyUnit: FrequencyUnit
}

export interface IUpdateCampaignNameAndBudgetMutation {
  accountId: string
  campaignId: string
  name: string
  budget: number
}

const campaignsTags = masterCampaignsApi.enhanceEndpoints({ addTagTypes: ['Campaigns', 'EndingOnByCampaignId', 'UsingUpdatedTargeting', 'RetargetingCampaigns', 'Campaign', 'CampaignApprovalQueue'] })

export const campaignsApi = campaignsTags.injectEndpoints({
  endpoints: build => ({
    getCampaigns: build.query<Campaign[], IGetCampaignsQuery>({
      query: ({ accountId, isAgency }) => {
        return {
          url: `accounts/${accountId}/campaigns`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'Campaigns', id: accountId }],
      async onQueryStarted({ accountId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(mobileDashboardApi.util.invalidateTags([{ type: 'MobileDashboard', id: accountId }]))
        } catch {}
      }
    }),
    getCampaign: build.query<Campaign, { accountId: string; campaignId: string }>({
      query: request => {
        return {
          url: `accounts/${request.accountId}/campaigns/${request.campaignId}`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, request) => [{ type: 'Campaign', id: request.campaignId }]
    }),
    getEndingOnDateByCampaignId: build.query<moment.Moment, IGetCampaignQuery>({
      query: ({ accountId, campaignId, isAgency }) => {
        return {
          url: `accounts/${accountId}/campaigns/${campaignId}/endingOnDate`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { campaignId }) => [{ type: 'EndingOnByCampaignId', id: campaignId }]
    }),
    getRetargetingCampaigns: build.query<Campaign[], IGetCampaignQuery>({
      query: ({ accountId, isAgency }) => {
        return {
          url: `accounts/${accountId}/campaigns/retargetingCampaigns`,
          method: 'GET',
          isContext: true,
          isAgency
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, { accountId }) => [{ type: 'RetargetingCampaigns', id: accountId }]
    }),

    usingUpdatedTargeting: build.query<boolean, { accountId: string; campaignId: string }>({
      query: request => {
        return {
          url: `accounts/${request.accountId}/campaigns/${request.campaignId}/usingUpdatedTargeting`,
          method: 'GET',
          isContext: true
        }
      },
      transformResponse: defaultTransformFn,
      providesTags: (result, error, request) => [{ type: 'UsingUpdatedTargeting', id: request.campaignId }]
    }),
    updateCampaignAdvancedTargeting: build.mutation<Campaign, { accountId: string; campaignId: string; advancedTargeting: UpdateAdvancedTargeting }>({
      query: ({ accountId, campaignId, advancedTargeting }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/advancedTargeting`,
        method: 'PUT',
        data: advancedTargeting,
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { accountId }) => [{ type: 'Campaigns', id: accountId }]
    }),
    updateCampaignTvTargeting: build.mutation<Campaign, { accountId: string; campaignId: string; tvTargeting: UpdateTvTargeting }>({
      query: ({ accountId, campaignId, tvTargeting }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/tvTargeting`,
        method: 'PUT',
        data: tvTargeting,
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { accountId }) => [{ type: 'Campaigns', id: accountId }]
    }),
    updateCampaignLocationTargeting: build.mutation<Campaign, { accountId: string; campaignId: string; targeting: IGeoTargeting; locationType: LocationType; audience: IAudienceTargeting; advancedOptions?: AdvancedTargetingOptions }>({
      query: ({ accountId, campaignId, targeting, locationType, audience, advancedOptions }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/updateLocationTargeting`,
        method: 'POST',
        data: {
          locations: targeting.locations,
          geoFences: targeting.geoFences,
          geoCircles: targeting.geoCircles,
          geoRectangles: targeting.geoRectangles,
          geoPolygons: targeting.geoPolygons,
          locationType: locationType,
          ageRanges: audience.ageRanges,
          genders: audience.genders,
          householdIncomes: audience.householdIncomes,
          audiences: audience.thirdPartyAudiences,
          segments: audience.segments,
          advancedTargetingOptions: advancedOptions
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn
    }),
    reactivateCampaign: build.mutation<Campaign, { accountId: string; campaignId: string; paymentMethodId: string }>({
      query: ({ accountId, campaignId, paymentMethodId }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/reactivateCampaign?paymentMethodId=${paymentMethodId}`,
        method: 'POST',
        data: {},
        isContext: true
      }),
      transformResponse: defaultTransformFn
    }),
    updateCampaignNameAndBudget: build.mutation<UpdateCampaignNameAndBudgetResponse, IUpdateCampaignNameAndBudgetMutation>({
      query: ({ accountId, campaignId, name, budget }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/updateCampaignNameAndBudget`,
        method: 'PUT',
        data: {
          name,
          budget
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId }) => [{ type: 'Campaign', id: campaignId }]
    }),
    updateCampaign: build.mutation<Campaign, CampaignUpdateRequest>({
      query: request => ({
        url: `accounts/${request.accountId}/campaigns/${request.campaignId}`,
        method: 'PUT',
        data: {
          name: request.name,
          budget: request.budget,
          startDate: request.startDate,
          endDate: request.endDate,
          maxCpm: request.maxCpm,
          totalIntervals: request.totalIntervals
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _, { campaignId, accountId }) =>
        !result
          ? []
          : result.masterCampaignId
          ? [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: campaignId },
              { type: 'MasterCampaignCampaigns', id: result.masterCampaignId }
            ]
          : [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: campaignId }
            ]
    }),
    updateSavedForLaterCampaign: build.mutation<Campaign, CampaignUpdateSavedForLaterRequest>({
      query: request => ({
        url: `accounts/${request.accountId}/campaigns/${request.campaignId}/updateSavedForLater`,
        method: 'PUT',
        data: {
          name: request.name,
          budget: request.budget,
          startDate: request.startDate,
          endDate: request.endDate,
          maxCpm: request.maxCpm,
          totalIntervals: request.totalIntervals,
          isIndefinite: request.isIndefinite,
          intervalUnit: request.intervalUnit,
          intervalUnitFrequency: request.intervalUnitFrequency
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _, { campaignId, accountId }) =>
        !result
          ? []
          : result.masterCampaignId
          ? [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: campaignId },
              { type: 'MasterCampaignCampaigns', id: result.masterCampaignId }
            ]
          : [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: campaignId }
            ]
    }),
    createCampaign: build.mutation<Campaign, { accountId: string; saveForLater: boolean; paymentInfo: PaymentInfo; campaignCreate: CampaignCreateWorkflow }>({
      query: request => ({
        url: `accounts/${request.accountId}/campaigns/create`,
        method: 'POST',
        data: {
          id: request.campaignCreate.campaignInfo.id,
          name: request.campaignCreate.campaignInfo.name,
          campaignType: request.campaignCreate.campaignType,
          budget: request.campaignCreate.campaignInfo.budget,
          contactName: request.campaignCreate.campaignInfo.contactName,
          phone: request.campaignCreate.campaignInfo.phone,
          email: request.campaignCreate.campaignInfo.email,
          startDate: request.campaignCreate.campaignInfo.startDate,
          endDate: request.campaignCreate.campaignInfo.endDate,
          isTotalBudget: request.campaignCreate.campaignInfo.isTotalBudget,
          campaignTargeting: request.campaignCreate.campaignTargeting,
          creativeInfo: request.campaignCreate.creativeInfo,
          businessInfo: request.campaignCreate.businessInfo,
          paymentMethodId: request.paymentInfo.paymentMethodType === AdPaymentType.Standard ? request.paymentInfo.paymentMethodId : undefined,
          paymentMethodType: request.paymentInfo.paymentMethodType,
          bundleId: request.paymentInfo.paymentMethodType === AdPaymentType.Bundle ? request.paymentInfo.bundleId : undefined,
          campaignSlotId: request.paymentInfo.paymentMethodType === AdPaymentType.Bundle ? request.paymentInfo.campaignSlotId : undefined,
          saveForLater: request.saveForLater,
          budgetFrequency: request.paymentInfo.budgetFrequency
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _, { accountId }) =>
        !result
          ? []
          : result.masterCampaignId
          ? [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: result.id },
              { type: 'MasterCampaignCampaigns', id: result.masterCampaignId }
            ]
          : [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: result.id }
            ]
    }),
    creatCampaignSimple: build.mutation<Campaign, { accountId: string; campaignName: string; campaignType: BuilderCampaignType; masterCampaignId?: string; isRetargeting?: boolean; isAudienceUpload?: boolean }>({
      query: request => ({
        url: `accounts/${request.accountId}/campaigns/createSimple`,
        method: 'POST',
        data: {
          campaignName: request.campaignName,
          campaignType: request.campaignType,
          masterCampaignId: request.masterCampaignId,
          isRetargeting: request.isRetargeting ?? false,
          isAudienceUpload: request.isAudienceUpload ?? false
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _, { accountId }) =>
        !result
          ? []
          : result.masterCampaignId
          ? [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: result.id },
              { type: 'MasterCampaignCampaigns', id: result.masterCampaignId }
            ]
          : [
              { type: 'Campaigns', id: accountId },
              { type: 'Campaign', id: result.id }
            ]
    }),
    updateCampaignDelivery: build.mutation<Campaign, { accountId: string; campaignId: string; delivery: CampaignDelivery; customerOptimizationStrategy: CustomerOptimizationStrategy }>({
      query: ({ accountId, campaignId, delivery, customerOptimizationStrategy }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/updateDelivery`,
        method: 'PUT',
        data: {
          inventorySettings: delivery.inventorySettings,
          deviceSettings: delivery.deviceSettings,
          frequency: delivery.frequency,
          frequencyUnit: delivery.frequencyUnit,
          dayParts: delivery.dayParts,
          categories: delivery.categories,
          placement: delivery.placement,
          specific: delivery.specific,
          customerOptimizationStrategy: customerOptimizationStrategy
        },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, request) => [
        { type: 'Campaign', id: request.campaignId },
        { type: 'Campaigns', id: request.accountId }
      ]
    }),
    approveCampaign: build.mutation<CampaignApprovalResult, { accountId: string; campaignId: string }>({
      query: ({ accountId, campaignId }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/approve`,
        method: 'POST',
        data: {},
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId, accountId }) => [{ type: 'Campaign', id: campaignId }, { type: 'Campaigns', id: accountId }, { type: 'CampaignApprovalQueue' }]
    }),
    disapproveCampaign: build.mutation<CampaignApprovalResult, { accountId: string; campaignId: string; reason: string }>({
      query: ({ accountId, campaignId, reason }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/disapprove`,
        method: 'POST',
        data: { reason },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId, accountId }) => [{ type: 'Campaign', id: campaignId }, { type: 'Campaigns', id: accountId }, { type: 'CampaignApprovalQueue' }]
    }),
    submitCampaignForApproval: build.mutation<CampaignApprovalResult, { accountId: string; campaignId: string }>({
      query: ({ accountId, campaignId }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/submitForApproval`,
        method: 'POST',
        data: {},
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId, accountId }) => [{ type: 'Campaign', id: campaignId }, { type: 'Campaigns', id: accountId }, { type: 'CampaignApprovalQueue' }]
    }),
    setCampaignAgencyReserveSettings: build.mutation<Campaign, { campaignId: string; accountId: string; agencyReserveSettings: ReserveSettings }>({
      query: ({ campaignId, accountId, agencyReserveSettings }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/agencyReserveSettings`,
        method: 'POST',
        isContext: true,
        data: {
          inheritAgencyReserve: agencyReserveSettings.inheritAgencyReserveRate,
          reserveRate: agencyReserveSettings.reserveRate
        }
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId, accountId }) => [
        { type: 'Campaigns', id: accountId },
        { type: 'Campaign', id: campaignId }
      ]
    }),
    updateTargetCpm: build.mutation<Campaign, { accountId: string; campaignId: string; targetCpm?: number }>({
      query: ({ accountId, campaignId, targetCpm }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/targetCpm`,
        method: 'PUT',
        data: { targetCpm },
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId, accountId }) => [
        { type: 'Campaign', id: campaignId },
        { type: 'Campaigns', id: accountId }
      ]
    }),
    updateAudienceFootprint: build.mutation<Campaign, { accountId: string; campaignId: string; footprint: AudienceFootprint }>({
      query: ({ accountId, campaignId, footprint }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}/audienceFootprint`,
        method: 'PUT',
        data: footprint,
        isContext: true
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, error, { campaignId, accountId }) => [
        { type: 'Campaign', id: campaignId },
        { type: 'Campaigns', id: accountId }
      ]
    })
  })
})

export const {
  useGetCampaignQuery,
  useGetEndingOnDateByCampaignIdQuery,
  useUpdateCampaignAdvancedTargetingMutation,
  useUpdateCampaignTvTargetingMutation,
  useGetRetargetingCampaignsQuery,
  useUpdateCampaignLocationTargetingMutation,
  useGetCampaignsQuery,
  useReactivateCampaignMutation,
  useUpdateCampaignNameAndBudgetMutation,
  useUpdateCampaignMutation,
  useUpdateSavedForLaterCampaignMutation,
  useCreatCampaignSimpleMutation,
  useUpdateCampaignDeliveryMutation,
  useSetCampaignAgencyReserveSettingsMutation,
  useApproveCampaignMutation,
  useDisapproveCampaignMutation,
  useSubmitCampaignForApprovalMutation,
  useUpdateAudienceFootprintMutation,
  useUpdateTargetCpmMutation
} = campaignsApi
